import { useState } from "react";
import axios from "axios";
import { FilePdf, UploadSimple, DownloadSimple, X, CheckCircle, WarningCircle, Eye } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PDF Tools → Sales Bill Extractor.
 * Same UX as Purchase-Bill extractor, but the extracted rows are download-only —
 * the backend never persists these into db.invoices (user creates their own
 * sales invoices in the app; this tool is just for pulling data OUT of PDFs
 * received from third-party systems).
 */
export default function SalesBillExtractor() {
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [downloadStats, setDownloadStats] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);

  const addFiles = (list) => {
    const arr = Array.from(list).filter((f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name));
    if (!arr.length) { setError("Please pick at least one .pdf file."); return; }
    setError(""); setFiles((prev) => [...prev, ...arr]);
    setPreview(null); setDownloadStats(null);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const reset = () => { setFiles([]); setPreview(null); setDownloadStats(null); setError(""); };

  const runPreview = async () => {
    if (!files.length) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData(); files.forEach((f) => fd.append("files", f, f.name));
      // Sales bill extractor reuses the Purchase preview because the parser is symmetric.
      const r = await axios.post(`${API}/pdf-tools/purchase-bills/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally { setBusy(false); }
  };

  const runExtract = async () => {
    if (!files.length) return;
    setBusy(true); setError(""); setJobProgress(null);
    try {
      const fd = new FormData(); files.forEach((f) => fd.append("files", f, f.name));
      // Async background job — handles 100+ pages without proxy timeout.
      const kick = await axios.post(`${API}/pdf-tools/sales-bills/extract-async`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      const jobId = kick.data.job_id;
      setJobProgress({ current: 0, total: 0, jobId });
      let done = false, lastStatus = "";
      while (!done) {
        await new Promise((r) => setTimeout(r, 2500));
        const s = await axios.get(`${API}/pdf-tools/jobs/${jobId}`);
        const p = s.data.progress || { current: 0, total: 0 };
        setJobProgress({ current: p.current, total: p.total, jobId });
        lastStatus = s.data.status;
        if (lastStatus === "done" || lastStatus === "error") done = true;
      }
      if (lastStatus === "error") {
        const s = await axios.get(`${API}/pdf-tools/jobs/${jobId}`);
        throw new Error(s.data.error || "Extraction failed");
      }
      const r = await axios.get(`${API}/pdf-tools/jobs/${jobId}/excel`, { responseType: "blob", timeout: 60000 });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url; a.download = `sales-bills-${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      setDownloadStats({ success: r.headers["x-bills-success"] || files.length, processed: r.headers["x-bills-processed"] || files.length });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Extract failed");
    } finally { setBusy(false); setJobProgress(null); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="pdf-tools-sales-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs font-bold uppercase tracking-widest">
          <FilePdf size={14} weight="fill" /> PDF Tools · Extract
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Sales Bill Extractor</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Pull structured data out of third-party sales invoice PDFs (either vector or scanned images)
          into a multi-sheet Excel workbook. Excel download only — nothing is saved to your app.
        </p>
      </div>

      <div className="bg-white border-2 border-dashed border-slate-300 rounded-sm p-8 text-center hover:border-slate-500 transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
      >
        <UploadSimple size={48} weight="duotone" className="mx-auto text-slate-400" />
        <p className="mt-3 text-sm text-slate-600">
          Drag & drop PDF files here, or
          <label className="ml-1 text-slate-900 font-bold underline cursor-pointer">
            browse
            <input type="file" accept="application/pdf,.pdf" multiple className="hidden"
              data-testid="pdf-file-input" onChange={(e) => addFiles(e.target.files)} />
          </label>
        </p>
      </div>

      {files.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900">{files.length} file{files.length > 1 ? "s" : ""} queued</h3>
            <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-900 underline" data-testid="pdf-clear-all-btn">Clear all</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-sm text-xs font-mono text-slate-700">
                <FilePdf size={14} weight="fill" className="text-rose-500" />
                <span className="max-w-[220px] truncate">{f.name}</span>
                <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-slate-900 ml-1"><X size={12} /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={runPreview} disabled={busy} data-testid="pdf-preview-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border border-slate-300 text-slate-800 text-sm font-bold hover:bg-slate-50 disabled:opacity-60">
            <Eye size={18} weight="bold" /> Preview
          </button>
          <button onClick={runExtract} disabled={busy} data-testid="pdf-extract-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-60">
            <DownloadSimple size={18} weight="bold" /> Extract & Download Excel
          </button>
        </div>
      )}

      {busy && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-sm p-4 text-sm text-blue-800" data-testid="sales-job-progress-banner">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="flex-1">Working — OCR + AI parsing…</span>
            {jobProgress && jobProgress.total > 0 && (
              <span className="font-mono text-xs font-bold">{jobProgress.current} / {jobProgress.total} pages</span>
            )}
          </div>
          {jobProgress && jobProgress.total > 0 && (
            <div className="mt-2 h-2 rounded-full bg-blue-100 overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${Math.min(100, Math.round((jobProgress.current / jobProgress.total) * 100))}%` }}
              />
            </div>
          )}
        </div>
      )}

      {error && <div className="mt-6 bg-rose-50 border border-rose-200 rounded-sm p-4 text-sm text-rose-800 flex items-center gap-2"><WarningCircle size={18} weight="fill" /><span>{error}</span></div>}

      {downloadStats && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm text-emerald-900 flex items-center gap-2" data-testid="pdf-download-success">
          <CheckCircle size={18} weight="fill" />
          <span>Excel downloaded — <b>{downloadStats.success}</b> parsed successfully</span>
        </div>
      )}

      {preview?.bills && (
        <div className="mt-8 space-y-3" data-testid="pdf-preview-results">
          {preview.bills.map((b, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="text-xs font-mono text-slate-500">{b.source}</div>
              {b.parsed ? (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div><b>{b.parsed.supplier_name}</b></div>
                  <div>Inv: {b.parsed.invoice_no}</div>
                  <div>Date: {b.parsed.invoice_date}</div>
                  <div>Total: Rs. {Number(b.parsed.total || 0).toFixed(2)}</div>
                </div>
              ) : <div className="text-sm text-rose-700 mt-1">{b.error}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
